import { useState } from 'react';

const heroBg = 'https://public.readdy.ai/ai/img_res/ab6f82bd9834cc97b643dd6239dded1c.jpg';

const socialLinks = [
  {
    id: 'youtube',
    label: 'YouTube',
    handle: '@chanactors',
    desc: '수업 현장 & 공연 영상',
    icon: 'ri-youtube-line',
    iconColor: 'text-red-400',
    borderColor: 'border-red-700/50',
    bg: 'bg-red-950/40',
    href: 'https://www.youtube.com/channel/UCFNAiF7UfmFZzAmOj9ZZE4A?si=vVjrNNYlwdleetPN',
    image: 'https://storage.readdy-site.link/project_files/6ba0cd05-38f2-4780-9d35-22df294407b6/bb02cc60-a783-44f2-8471-d8931dd0c840_KakaoTalk_20260323_180651621_01.jpg?v=9cf13f242ee77741162e56ea16c71c6e',
  },
  {
    id: 'instagram',
    label: 'Instagram',
    handle: '@chan_actors',
    desc: '일상 & 수업 스케치',
    icon: 'ri-instagram-line',
    iconColor: 'text-rose-400',
    borderColor: 'border-rose-700/50',
    bg: 'bg-rose-950/40',
    href: 'https://www.instagram.com/chan_actors',
    image: 'https://storage.readdy-site.link/project_files/6ba0cd05-38f2-4780-9d35-22df294407b6/06bbd083-2a05-407a-9fca-5e3cc0ffa460_123123.jpg?v=da3defe706fc9d821306261bf6a4b4b7',
  },
  {
    id: 'blog',
    label: 'Naver Blog',
    handle: 'chanactors',
    desc: '입시 정보 & 합격 후기',
    icon: 'ri-article-line',
    iconColor: 'text-green-400',
    borderColor: 'border-green-700/50',
    bg: 'bg-green-950/40',
    href: 'https://blog.naver.com/chanactors',
    image: 'https://storage.readdy-site.link/project_files/6ba0cd05-38f2-4780-9d35-22df294407b6/6013a476-e4a8-44d4-9bc0-8af571d8de2f_unnamed.jpg?v=d2c8860294043b1ca22ea9ad6b66a48f',
  },
];

export default function HeroSection() {
  const [hovered, setHovered] = useState<string | null>(null);

  const handleScroll = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${heroBg})` }}
      />

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/50 to-black/75" />

      {/* Content */}
      <div className="relative z-10 w-full flex flex-col items-center justify-center text-center px-6 pt-24 pb-16">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" />
          <span className="text-white/90 text-xs font-medium tracking-widest uppercase">
            실력과 더불어 인성까지 바르게 가르칩니다
          </span>
        </div>

        {/* Main Title */}
        <h1 className="font-serif-kr font-black text-white leading-tight mb-6">
          <span className="block text-5xl md:text-7xl lg:text-8xl tracking-tight">찬연기아카데미</span>
        </h1>

        <p className="text-white/70 text-base md:text-lg max-w-xl mx-auto mb-10 font-light leading-relaxed">
          입시부터 취미까지, 당신의 꿈을 무대 위에서<br />
          찬란하게 빛내세요
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-4 mb-16">
          <button
            onClick={() => handleScroll('programs')}
            className="px-8 py-3.5 rounded-full bg-red-800 text-white font-semibold text-sm hover:bg-red-900 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            프로그램 둘러보기 <i className="ri-arrow-right-line ml-1" />
          </button>
          <button
            onClick={() => handleScroll('contact')}
            className="px-8 py-3.5 rounded-full bg-white/10 border border-white/30 text-white font-semibold text-sm hover:bg-white/20 transition-all duration-300 cursor-pointer whitespace-nowrap backdrop-blur-sm"
          >
            입시/입학상담 신청
          </button>
        </div>

        {/* Social Media Cards */}
        <div className="flex items-stretch justify-center gap-4 w-full max-w-3xl mx-auto">
          {socialLinks.map((social) => (
            <a
              key={social.id}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className={`relative flex-1 rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 border ${social.borderColor} backdrop-blur-sm`}
              style={{ minWidth: 0 }}
              onMouseEnter={() => setHovered(social.id)}
              onMouseLeave={() => setHovered(null)}
            >
              {/* Image */}
              <div className="relative h-40 overflow-hidden">
                <img
                  src={social.image}
                  alt={`${social.label} 찬연기아카데미`}
                  className={`w-full h-full object-cover object-top transition-transform duration-700 ${
                    hovered === social.id ? 'scale-110' : 'scale-100'
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                {/* Center icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm border border-white/15">
                    <i className={`text-2xl ${social.iconColor} ${social.icon}`} />
                  </div>
                </div>
              </div>

              {/* Text */}
              <div className={`px-4 py-3 ${social.bg} backdrop-blur-sm`}>
                <p className={`text-xs font-bold ${social.iconColor} mb-0.5`}>{social.label}</p>
                <p className="text-white/80 text-xs font-medium">{social.handle}</p>
                <p className="text-white/50 text-xs mt-1">{social.desc}</p>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/50 text-xs tracking-widest">SCROLL</span>
        <i className="ri-arrow-down-line text-white/50 text-lg" />
      </div>
    </section>
  );
}
